import { useCallback, useMemo, useState } from "react";

import BoardShareActionItemView from "./BoardShareActionItemView";

import { BOARD_ROLE_LABELS } from "@/features/player-kanban/constants/assignableBoardRoles";

import { UserKanbanBoardRole } from "@/generated/graphql";

/**
 * Gets the display text for a role
 * @param role - The user role
 */
export const getRoleDisplayText = (role: UserKanbanBoardRole): string =>
  BOARD_ROLE_LABELS[role] ?? "Select role";

/**
 * Props for BoardShareActionItem component
 */
export interface BoardShareActionItemProps {
  userKanbanBoardId: string;
  userEmail: string;
  userName?: string;
  currentRole: UserKanbanBoardRole;
  isCurrentUser: boolean;
  /**
   * Whether the viewer may change roles and remove members at all.
   *
   * SE-8296: this is NOT the same authority as transferring ownership.
   * `addUserToKanbanBoard` and `updateUserKanbanBoardRole` are junction-OWNER
   * only server-side, while `transferKanbanBoardOwnership` also admits org
   * MANAGER/ADMIN/OWNER. An org admin can therefore open this sheet to repair
   * ownership while having no authority over membership, and these controls
   * must stay out of their reach rather than fail on press.
   */
  canManageMembers: boolean;
  onRoleChange: (
    userKanbanBoardId: string,
    newRole: UserKanbanBoardRole
  ) => void;
  onRemove: (userKanbanBoardId: string) => void;
}

/**
 * Container component for BoardShareActionItem - manages business logic for a single user share item
 * @param props - Component props
 */
const BoardShareActionItemContainer = (props: BoardShareActionItemProps) => {
  const roleDisplayText = useMemo(
    () => getRoleDisplayText(props.currentRole),
    [props.currentRole]
  );

  const handleSelectEditor = useCallback(() => {
    props.onRoleChange(props.userKanbanBoardId, UserKanbanBoardRole.Editor);
  }, [props]);

  const handleSelectViewer = useCallback(() => {
    props.onRoleChange(props.userKanbanBoardId, UserKanbanBoardRole.Viewer);
  }, [props]);

  const handleRemove = useCallback(() => {
    props.onRemove(props.userKanbanBoardId);
  }, [props]);

  /**
   * SE-8296: the OWNER row is read-only. The server answers
   * BadRequestException("Cannot change the owner's role") for any edit to the
   * incumbent's row, and removal is refused too, so the row displays the role
   * and offers nothing. Ownership moves via the Transfer ownership control.
   */
  const isOwnerRow = props.currentRole === UserKanbanBoardRole.Owner;
  const isRowEditable = props.canManageMembers && !isOwnerRow;

  // SE-5570: controlled open state for the shared SelectDropdown surface. The
  // current user cannot change their own role, so their trigger is inert.
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => {
    if (props.isCurrentUser || !isRowEditable) return;
    setIsOpen(prev => !prev);
  }, [props.isCurrentUser, isRowEditable]);
  const onClose = useCallback(() => setIsOpen(false), []);

  return (
    <BoardShareActionItemView
      userEmail={props.userEmail}
      userName={props.userName}
      currentRole={props.currentRole}
      isCurrentUser={props.isCurrentUser}
      isRoleEditable={isRowEditable && !props.isCurrentUser}
      isRemovable={isRowEditable && !props.isCurrentUser}
      roleDisplayText={roleDisplayText}
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      onSelectEditor={handleSelectEditor}
      onSelectViewer={handleSelectViewer}
      onRemove={handleRemove}
    />
  );
};

BoardShareActionItemContainer.displayName = "BoardShareActionItemContainer";

export default BoardShareActionItemContainer;
